/**
 * Stripe utility functions - using Supabase edge functions
 * These functions provide subscription management through Supabase edge functions
 */

import { supabase } from '@/lib/supabase/client';
import { type ServiceResult, serviceSuccess, serviceError } from '@/lib/utils/errorUtils';

interface SubscriptionData {
  id: string;
  status: string;
  cancel_at_period_end: boolean;
  current_period_end: number;
  items: {
    data: Array<{
      price: {
        id: string;
        unit_amount: number;
        currency: string;
      };
    }>;
  };
}

interface SubscriptionResult {
  subscription: SubscriptionData | null;
}

interface PortalSessionResult {
  url: string;
}

interface CheckoutCustomerInfo {
  email?: string;
  name?: string;
}

interface StripeSubscriptionConfig {
  subscription?: {
    id?: string;
    status?: string;
    cancel_at_period_end?: boolean;
    current_period_end?: number;
    customer?: string;
    items?: {
      data?: Array<{
        price?: {
          id?: string;
          unit_amount?: number;
          currency?: string;
          recurring?: Record<string, string | number | boolean>;
        };
      }>;
    };
  };
  planTier?: string;
  customerId?: string;
  product?: {
    name?: string;
  };
  price?: {
    unit_amount?: number;
    currency?: string;
    recurring?: Record<string, string | number | boolean>;
  };
}


/**
 * Get user subscription using edge function
 * @param userId User ID
 * @returns Subscription result
 */
export async function getUserSubscription(userId: string): Promise<SubscriptionResult> {
  try {
    const response = await supabase.functions.invoke<StripeSubscriptionConfig>('get-subscription', {
      body: {
        entityId: userId,
        entityType: 'user',
      },
    });
    const data = response.data;
    const error = response.error as Error | null;

    if (error ?? !data?.subscription) {
      return { subscription: null };
    }

    // Transform the edge function response to match the expected interface
    const sub = data.subscription;
    const subscription = {
      id: sub.id ?? '',
      status: sub.status ?? 'inactive',
      cancel_at_period_end: sub.cancel_at_period_end ?? false,
      current_period_end: sub.current_period_end ?? 0,
      items: {
        data: (sub.items?.data ?? []).map(item => ({
          price: {
            id: item.price?.id ?? '',
            unit_amount: item.price?.unit_amount ?? 0,
            currency: item.price?.currency ?? 'usd'
          }
        }))
      }
    };

    // Update user profile with subscription data if subscription is active
    if (subscription.status === 'active' && data.planTier) {
      try {
        const { error: updateError } = await supabase
          .from('profiles')
          .update({
            plan_tier: data.planTier,
            subscription_status: subscription.status,
            stripe_subscription_id: subscription.id,
            stripe_customer_id: data.customerId ?? sub.customer ?? null,
            current_period_end: subscription.current_period_end ? new Date(subscription.current_period_end * 1000).toISOString() : null
          })
          .eq('id', userId);

        if (updateError) {
          console.warn('Failed to sync user subscription data:', updateError);
        }
      } catch (syncError) {
        console.warn('Error syncing user subscription data:', syncError);
      }
    }

    return { subscription };
  } catch (error) {
    console.error('Error fetching user subscription:', error);
    return { subscription: null };
  }
}

/**
 * Sync user subscription data from Stripe
 * @param userId User ID
 * @returns Success result
 */
export async function syncUserSubscription(userId: string): Promise<{ success: boolean; message: string }> {
  try {
    const result = await getUserSubscription(userId);
    if (result.subscription) {
      return { success: true, message: 'Subscription synced successfully' };
    }
    return { success: false, message: 'No active subscription found' };
  } catch (error) {
    console.error('Error syncing user subscription:', error);
    return { success: false, message: 'Failed to sync subscription' };
  }
}

/**
 * Create billing portal session using edge function
 * @param type Session type (user or organization)
 * @param id Entity ID
 * @returns Portal session result
 */
export async function createPortalSession(type: string, id: string): Promise<ServiceResult<PortalSessionResult>> {
  try {
    // Get the Supabase URL from environment variable
    const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
    if (!supabaseUrl) {
      return serviceError<PortalSessionResult>(new Error('Supabase URL not configured'), 'Error creating portal session');
    }

    const session = await supabase.auth.getSession();
    if (!session.data.session?.access_token) {
      return serviceError<PortalSessionResult>(new Error('No authentication session found'), 'Error creating portal session');
    }

    const response = await fetch(`${supabaseUrl}/functions/v1/create-portal-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.data.session.access_token}`,
      },
      body: JSON.stringify({
        entityType: type === 'organization' ? 'organization' : 'user',
        entityId: id,
      }),
    });

    const responseData = await response.json() as { url?: string; error?: string; message?: string };

    if (!response.ok) {
      console.error('Portal session error response:', responseData);
      return serviceError<PortalSessionResult>(
        new Error(responseData.error ?? responseData.message ?? 'Failed to create portal session'),
        'Error creating portal session'
      );
    }

    if (!responseData.url) {
      console.error('Portal session response:', responseData);
      return serviceError<PortalSessionResult>(
        new Error('Invalid response from create-portal-session'),
        'Error creating portal session'
      );
    }

    return serviceSuccess({ url: responseData.url });
  } catch (error) {
    return serviceError<PortalSessionResult>(error, 'Error creating portal session');
  }
}

/**
 * Create checkout session using edge function
 * @param priceId Stripe price ID
 * @param returnUrl Return URL after successful payment
 * @returns Checkout session URL
 */
export async function createCheckoutSession(
  priceId: string,
  returnUrl: string,
  customerInfo?: CheckoutCustomerInfo,
  cancelUrl?: string
): Promise<ServiceResult<{ url: string }>> {
  try {
    const normalizedEmail = typeof customerInfo?.email === 'string' ? customerInfo.email.trim() : '';
    const normalizedName = typeof customerInfo?.name === 'string' ? customerInfo.name.trim() : '';

    const response = await supabase.functions.invoke<{ url: string }>('create-payment-link', {
      body: {
        priceId,
        successUrl: returnUrl,
        cancelUrl: cancelUrl ?? returnUrl,
        ...(normalizedEmail ? { email: normalizedEmail } : {}),
        ...(normalizedName ? { name: normalizedName } : {}),
      },
    });
    const data = response.data as { url: string } | null;
    const error = response.error as Error | null;

    if (error) {
      return serviceError<{ url: string }>(
        new Error(error.message || 'Failed to create checkout session'),
        'Error creating checkout session'
      );
    }

    if (!data?.url) {
      return serviceError<{ url: string }>(new Error('No checkout URL returned from payment service'), 'Error creating checkout session');
    }

    return serviceSuccess({ url: data.url });
  } catch (error) {
    return serviceError<{ url: string }>(error, 'Error creating checkout session');
  }
}

interface PaymentStatusData {
  session_status?: string;
  payment_status?: string;
  mode?: string;
  subscription?: {
    id: string;
    status: string;
    plan_name: string;
    amount: number;
    currency: string;
    interval: string;
    current_period_end?: string;
  };
  message?: string;
}

interface VerifyCheckoutResponse {
  status: string;
  mode?: string;
  plan?: {
    tier: string;
    name: string;
    storage_gb: number;
    guest_limit: number;
    price_amount: number;
    currency: string;
  };
  subscription_id?: string;
  message?: string;
}

/**
 * Check payment status after checkout completion.
 * Calls verify-checkout edge function which verifies the Stripe session
 * and activates the plan if payment was successful.
 */
export async function checkPaymentStatus(
  sessionId: string,
  _entityId?: string | null,
  _entityType?: 'user' | 'organization'
): Promise<ServiceResult<PaymentStatusData>> {
  try {
    if (!sessionId) {
      return serviceError<PaymentStatusData>(new Error('Session ID is required'), 'Error checking payment status');
    }

    // Call verify-checkout edge function — it verifies the Stripe session
    // and activates the plan directly (no webhook dependency)
    const maxAttempts = 3;
    const delayMs = 2000;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const response = await supabase.functions.invoke<VerifyCheckoutResponse>('verify-checkout', {
        body: { sessionId },
      });
      const data = response.data;
      const error = response.error as Error | null;

      if (error) {
        console.error('verify-checkout error:', error);
        if (attempt === maxAttempts - 1) {
          return serviceError<PaymentStatusData>(new Error(String(error)), 'Error verifying checkout');
        }
        await new Promise(resolve => setTimeout(resolve, delayMs));
        continue;
      }

      if (data?.status === 'active') {
        // Plan activated successfully
        const plan = data.plan;
        return serviceSuccess<PaymentStatusData>({
          session_status: 'complete',
          payment_status: 'paid',
          mode: data.mode ?? 'payment',
          subscription: plan ? {
            id: data.subscription_id ?? sessionId,
            status: 'active',
            plan_name: plan.name,
            amount: plan.price_amount,
            currency: plan.currency,
            interval: 'one_time',
          } : undefined,
          message: data.message ?? 'Payment verified successfully'
        });
      }

      if (data?.status === 'pending' || data?.status === 'unpaid') {
        // Payment not yet complete — retry
        if (attempt < maxAttempts - 1) {
          await new Promise(resolve => setTimeout(resolve, delayMs));
          continue;
        }

        return serviceError<PaymentStatusData>(
          new Error(data.message ?? (data.status === 'pending'
            ? 'Payment is still processing. Please refresh in a moment.'
            : 'Payment has not been completed.')),
          'Error verifying checkout'
        );
      }

      return serviceError<PaymentStatusData>(
        new Error(data?.message ?? 'Checkout session verified, but no active plan was found.'),
        'Error verifying checkout'
      );
    }

    return serviceError<PaymentStatusData>(
      new Error('Payment verification timed out. Please refresh in a moment.'),
      'Error verifying checkout'
    );
  } catch (error) {
    return serviceError<PaymentStatusData>(error, 'Error checking payment status');
  }
}

/**
 * Find and populate Stripe customer ID for a user
 * This is a workaround for when the customer ID is missing from the database
 * @param userId User ID
 * @returns Success result with customer ID if found
 */
export async function findAndPopulateCustomerId(userId: string): Promise<{ success: boolean; customerId?: string; message: string }> {
  try {
    // First check if customer ID already exists
    const { data: profile } = await supabase
      .from('profiles')
      .select('stripe_customer_id, email')
      .eq('id', userId)
      .single();

    if (profile?.stripe_customer_id) {
      return {
        success: true,
          customerId: profile.stripe_customer_id,
        message: 'Customer ID already exists'
      };
    }

    // If not, try to sync subscription which will populate it
    const syncResult = await syncUserSubscription(userId);
    
    if (syncResult.success) {
      // Check again after sync
      const { data: updatedProfile } = await supabase
        .from('profiles')
        .select('stripe_customer_id')
        .eq('id', userId)
        .single();

      if (updatedProfile?.stripe_customer_id) {
        return {
          success: true,
          customerId: updatedProfile.stripe_customer_id,
          message: 'Customer ID found and populated'
        };
      }
    }

    return { 
      success: false, 
      message: 'Unable to find Stripe customer ID. Please ensure you have an active subscription.' 
    };
  } catch (error) {
    console.error('Error finding customer ID:', error);
    return { 
      success: false, 
      message: 'Error finding customer ID' 
    };
  }
}
